import clsx from 'clsx';
import _ from 'lodash';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { useAllTrendStoreItems } from '@/core/hooks/useAllTrendStoreItems.hook';
import { addTrendItem } from '@/trendData/trend.actions';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { ItemOutputWithLaneAndAxis } from '@/tools/models/model.utilities';
import type { TooltipPosition } from '@seeqdev/qomponents';

interface AddToDisplayPaneIF {
  itemId: string;
  tooltip?: string;
  extraClassNames?: string;
  tooltipPlacement?: TooltipPosition;
  afterAdd?: (itemId: string) => Promise<void> | void;
  outputItemId?: string;
  additionalProps?: Record<string, unknown>;
  getLaneAndAxisAlign?: (itemId: string) => [number, string] | null;
}

export const AddToDisplayPane = (props: AddToDisplayPaneIF) => {
  const {
    itemId,
    tooltip,
    extraClassNames,
    tooltipPlacement,
    afterAdd,
    outputItemId,
    additionalProps,
    getLaneAndAxisAlign,
  } = props;

  const trendItems = useAllTrendStoreItems();

  if (_.some(trendItems, { id: itemId })) {
    return null;
  }

  const addToDisplay = () => {
    let allProps: Record<string, unknown> | undefined;
    if (outputItemId) {
      const outputItem = _.find(trendItems, { id: outputItemId });
      allProps = {
        tableId: _.get(outputItem, 'tableId'),
        rowId: _.get(outputItem, 'rowId'),
      };
    }
    if (additionalProps) {
      allProps = { ...(allProps || {}), ...additionalProps };
    }
    sqItemsApi
      .getItemAndAllProperties({ id: itemId })
      .then(({ data: item }) => {
        const [lane, axisAlign] = getLaneAndAxisAlign?.(item.id) ?? [];
        const updatedItem = lane && axisAlign ? ({ ...item, lane, axisAlign } as ItemOutputWithLaneAndAxis) : item;
        return addTrendItem(updatedItem, allProps);
      })
      .then(() => (afterAdd ? afterAdd(itemId) : undefined));
  };

  return (
    <HoverTooltip placement={tooltipPlacement || 'right'} text={tooltip || 'ADD_TO_DISPLAY'}>
      <a
        data-testid="addToDisplay"
        className={clsx(
          'displayIcon a fa-sharp fa-regular fa-chart-area link-no-underline animated animate-if-opacity pt2 pl5' +
            ' force-link-color',
          extraClassNames,
        )}
        onClick={addToDisplay}
      />
    </HoverTooltip>
  );
};
